import {
  CatCheckoutDetails,
  IFreshBasketData,
  IStoredFreshBasket,
} from "@checkout/types";
// Extended type to match the actual API structure
import type {
  TCatFormDataInput,
  TUserFormData,
  TDeliveryData,
  TSubscriptionData,
  TAddressFormData,
} from "@./suf-store";
import { IActionRequestTrackingInfo } from "@forms/schema";
import { BodyType, Breed, Lifestyle } from "@cats/types";
import { Gender } from "@./cat-calculations";
import {
  FreshMealPlanType,
  TrialType,
} from "libs/api/customer/src/lib/types/checkout";
import {
  calculateCatBenefits,
  normaliseCatsFormDataForCheckout,
} from "@./state";
import { PricingPlan } from "@./suf-store";
import { FormName } from "apps/website/types/form";

interface BuildFreshBasketParams {
  cats: Record<string, TCatFormDataInput>;
  user: TUserFormData;
  delivery: TDeliveryData;
  subscription: TSubscriptionData;
  formUsed: FormName;
  trackingInfo?: IActionRequestTrackingInfo;
  isShreddedRecipeTestVariant?: boolean;
}

/**
 * Maps a fresh meal plan product type to the corresponding trial type
 */
function productToTrialType(product: FreshMealPlanType): TrialType {
  switch (product) {
    case "FRESH":
      return "NOT_A_TRIAL";
    case "FRESH_TRIAL":
      return "TWENTY_OFF";
    case "FRESH_CROSS_SELL":
      return "TWENTY_OFF_CROSS_SELL";
    default:
      return "NOT_A_TRIAL";
  }
}

/**
 * Maps age fields based on the age mode
 */
function mapAgeFields(age?: { mode?: string; years?: number; months?: number; weeks?: number }) {
  if (!age?.mode) {
    return {
      catAgeYears: undefined,
      catAgeMonths: undefined,
      catAgeWeeks: undefined,
    };
  }

  return {
    catAgeYears: age.mode === "years" ? age.years : undefined,
    catAgeMonths: age.mode === "months" ? age.months : undefined,
    catAgeWeeks: age.mode === "weeks" ? age.weeks : undefined,
  };
}

/**
 * Trio bundle quantity: 1 if the primary cat has a trio bundle upsell, 0 otherwise.
 * The backend handles splitting the bundle into individual pouches for subscription creation.
 */
function getNibblesTrioBundleQuantity(subscription: TSubscriptionData, isPrimary: boolean): number {
  if (!isPrimary || !subscription.nibblesUpsell?.added) {
    return 0;
  }
  return subscription.nibblesUpsell.type === "NIBBLES_TRIO_BUNDLE" ? 1 : 0;
}

/**
 * Builds a checkout basket address object with fallback values
 * Used for: Checkout basket creation (billingAddress/shippingAddress in basket)
 * Format: { firstName, lastName, address1, address2, city, country, phone, postcode }
 */
export function buildAddressObject(address: TAddressFormData | undefined, defaultCountry: string, user: TUserFormData) {
  return {
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    address1: address?.address1 || "",
    address2: address?.address2 || "",
    city: address?.city || "",
    country: address?.countryCode || defaultCountry,
    phone: address?.phoneNumber || "",
    postcode: address?.postcode || "",
  };
}

/**
 * Builds the API-format address (address.billingAddress/address.shippingAddress).
 * Format: { line1, line2, city, country, phone, postcode }
 */
export function buildApiAddressObject(address: TAddressFormData | undefined, defaultCountry: string) {
  return {
    city: address?.city || "",
    line1: address?.address1 || "",
    line2: address?.address2 || "",
    phone: address?.phoneNumber || "",
    country: address?.countryCode || defaultCountry,
    postcode: address?.postcode || "",
  };
}

/**
 * Builds the nested address structure for external API calls (i.e Stripe)
 * Used for: API integration where addresses need line1/line2 format
 * Creates both billing and shipping addresses using the API format
 */
function buildNestedAddressStructure(user: TUserFormData) {
  return {
    billingAddress: buildApiAddressObject(user.billingAddress, "GB"),
    shippingAddress: buildApiAddressObject(user.shippingAddress, "GB"),
  };
}

/**
 * Maps basic cat information
 */
function mapBasicCatInfo(cat: TCatFormDataInput) {
  return {
    name: cat.name || "",
    birthday: cat.birthday || "",
    bodyType: (cat.body?.bodyType as BodyType) || "UNKNOWN",
    breed: (cat.breed as Breed) || "UNKNOWN",
    gender: (cat.gender as Gender) || "UNKNOWN",
    isBirthdayPrecise: false,
    isNeutered: cat.isNeutered || false,
    lifestyle: (cat.body?.activityLevel as Lifestyle) || "UNKNOWN",
    weight: cat.weight?.valueInKg?.toString() || "0",
    id: undefined,
    catLifeStage: cat.lifeStage,
    catPlanRemoved: cat.softDelete || false,
  };
}

/**
 * Maps cat diet-related information
 */
function mapCatDietInfo(cat: TCatFormDataInput) {
  return {
    brand: cat.diet?.current?.brand || "UNKNOWN",
    feedList: cat.diet?.current?.type?.map((it) => it.toUpperCase()) || [],
    fussiness: cat.diet?.eatingHabits || "UNKNOWN",
    allergyList: cat.diet?.foodAllergies?.allergens?.map((it) => it.toUpperCase()) || [],
    catHasAllergies: cat.diet?.foodAllergies?.hasAllergies || false,
    ...calculateCatBenefits(cat),
  };
}

/**
 * Maps cat medical information
 */
function mapCatMedicalInfo(cat: TCatFormDataInput) {
  return {
    medicalConditions: cat.medical?.conditions || [],
    medicalNotes: "",
    catHasMedicalConditions: cat.medical?.hasConditions || false,
  };
}

/**
 * Maps recipe information
 */
function mapRecipeInfo(cat: TCatFormDataInput) {
  return {
    recipeTitleToQuantity: cat.selectedRecipes?.recipes?.reduce((acc, recipe) => {
      acc[recipe.type] = recipe.quantity;
      return acc;
    }, {} as Record<string, number>) || {},
  };
}


/**
 * Gets transformed active cats from the cats record
 */
function getTransformedActiveCats(
  cats: Record<string, TCatFormDataInput>,
  subscription: TSubscriptionData,
): CatCheckoutDetails[] {
  const activeCats = Object.values(cats).filter((cat) => !cat.softDelete);
  return activeCats.map((cat, index) => {
    const isPrimary = index === 0;
    return transformCatToCheckoutDetails(cat, subscription, isPrimary);
  });
}

/**
 * Constructs the basket object with all required properties
 */
// @TODO: Investigate why we populate the address twice (nested vs top-level) and clean up.
function constructFreshBasketObject(
  transformedCats: CatCheckoutDetails[],
  user: TUserFormData,
  delivery: TDeliveryData,
  subscription: TSubscriptionData,
  formUsed: string,
  trackingInfo?: IActionRequestTrackingInfo,
  isShreddedRecipeTestVariant?: boolean,
): IStoredFreshBasket {
  return {
    type: "FRESH",
    deliveryDate: delivery.deliveryDate || "",
    discountCode: subscription.discount.discountCode || "",
    postcode: user.shippingAddress?.postcode || "",
    billingAddress: buildAddressObject(user.billingAddress, "GB", user),
    shippingAddress: buildAddressObject(user.shippingAddress, "GB", user),
    metadata: {
      formUsed,
      fbc: trackingInfo?.fbc,
      fbp: trackingInfo?.fbp,
      ttclid: trackingInfo?.ttclid,
      _ttp: trackingInfo?._ttp,
      test_event_code: trackingInfo?.test_event_code,
      pageUrl: trackingInfo?.pageUrl,
    },
    cats: transformedCats,
    trialType: productToTrialType(subscription.productType || "FRESH_TRIAL"),
    catsInHousehold: transformedCats.length,
    email: user.email || "",
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    customerId: user.oldId || "",
    catData: transformedCats,
    address: buildNestedAddressStructure(user),
    isShreddedRecipeTestVariant,
  };
}

/**
 * Transforms a cat form data object to checkout details
 */
function transformCatToCheckoutDetails(
  cat: TCatFormDataInput,
  subscription: TSubscriptionData,
  isPrimary: boolean,
): CatCheckoutDetails {
  const ageFields = mapAgeFields(cat.age);
  const basicInfo = mapBasicCatInfo(cat);
  const dietInfo = mapCatDietInfo(cat);
  const medicalInfo = mapCatMedicalInfo(cat);
  const recipeInfo = mapRecipeInfo(cat);

  return {
    ...basicInfo,
    ...dietInfo,
    ...medicalInfo,
    ...recipeInfo,
    ...ageFields,
    nibblesTrioBundleQuantity: getNibblesTrioBundleQuantity(subscription, isPrimary),
    trialMealPlan: subscription.trial?.planType ?? PricingPlan.TRIAL,
    ongoingMealPlan: subscription.ongoing?.planType ?? PricingPlan.STANDARD,
  };
}

/**
 * Builds a fresh checkout basket from signup flow data
 *
 * @param params - The signup flow data including cats, user, delivery, subscription, and trackingInfo
 * @returns A fresh checkout basket ready for the checkout API
 *
 */
export function buildFreshBasket(params: BuildFreshBasketParams): IFreshBasketData {
  const { cats, user, delivery, subscription, formUsed, trackingInfo, isShreddedRecipeTestVariant } = params;
  const normalisedCats = normaliseCatsFormDataForCheckout(cats);

  const transformedCats = getTransformedActiveCats(normalisedCats, subscription);
  const basket = constructFreshBasketObject(
    transformedCats, user, delivery, subscription,
    formUsed, trackingInfo, isShreddedRecipeTestVariant,
  );

  return { basket };
}
